import { useMemo, useRef } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { unifyRepeatsFromBase, applyPBR, loadTextureMaps, swapMeshUV } from './textureUtils'
import { useAppStore } from '../../stores/appStore'

function TrayCmp({ trayUrl, texture, useUvChecker, rotateQuarterTurns, swapUV }: { 
  trayUrl: string
  texture: any
  useUvChecker: boolean
  rotateQuarterTurns: number
  swapUV: boolean
}) {
  const trayModel = useLoader(FBXLoader, trayUrl)
  const BASE_SCALE = 0.05

  const bedVariation = useAppStore((s) => s.bedConfig.bed.variation)
  const isAdjustable = bedVariation?.bed_type_value === 'adjustable'

  // Get bed bounding box and topper base/top Y (both in bed group local space)
  const bedBox = (window as any).__bed_bbox__
  const topperBaseY = (window as any).__topper_base_y__
  const topperTopY = (window as any).__topper_top_y__

  if (!bedBox || bedBox.isEmpty()) return null as any

  // Store bedBox.max.y in a ref so it stays stable (bedBox object might be modified elsewhere)
  const bedBoxMaxYRef = useRef<number | null>(null)
  if (bedBox && !bedBox.isEmpty() && bedBoxMaxYRef.current === null) {
    bedBoxMaxYRef.current = bedBox.max.y
  }

  // Calculate tray position - use topper top if available, otherwise fall back to bed top
  const bedCenterX = (bedBox.min.x + bedBox.max.x) / 2
  const bedCenterZ = (bedBox.min.z + bedBox.max.z) / 2
  
  // Clone and setup tray
  const clone = useMemo(() => {
    const c = trayModel.clone()
    c.scale.setScalar(BASE_SCALE)
    
    const trayBox = new THREE.Box3().setFromObject(trayModel)
    const trayCenter = trayBox.getCenter(new THREE.Vector3())
    const trayMin = trayBox.min
    
    c.position.set(-trayCenter.x * BASE_SCALE, -trayMin.y * BASE_SCALE, -trayCenter.z * BASE_SCALE)
    
    // Apply textures if available
    if (texture?.base_color_url || texture?.base_color_urls) {
      c.traverse((child: any) => {
        if (child.isMesh && child.material) {
          swapMeshUV(child, swapUV, `tray`)
          const mat = new THREE.MeshStandardMaterial()
          mat.color = new THREE.Color(0xffffff)
          ;(mat as any).emissive?.setRGB?.(0, 0, 0)
          ;(mat as any).vertexColors = false
          const turns: number = Math.abs(rotateQuarterTurns || 0) % 4
          
          const loader = new THREE.TextureLoader()
          const maps = loadTextureMaps(texture, loader)
          
          if (maps.base) {
            maps.base.flipY = false
            // @ts-ignore
            if ((THREE as any).SRGBColorSpace) {
              // @ts-ignore
              maps.base.colorSpace = (THREE as any).SRGBColorSpace
            }
            maps.base.wrapS = maps.base.wrapT = THREE.RepeatWrapping
            if ((maps.base as any).center && typeof (maps.base as any).center.set === 'function') {
              ;(maps.base as any).center.set(0.5, 0.5)
            }
            maps.base.rotation = turns ? (Math.PI / 2) * turns : 0
            maps.base.wrapS = THREE.RepeatWrapping
            maps.base.repeat.x = 1
            maps.base.offset.x = 0
          }
          
          applyPBR({
            mat,
            origMat: child.material,
            type: 'wood',
            meshName: child.name,
            maps,
            rotateTurns: turns,
            hasUV2: swapUV
          })
          child.material = mat
          unifyRepeatsFromBase(child.material)
          mat.needsUpdate = true
        }
      })
    }

    c.traverse((child: any) => {
      if (child.isMesh) {
        child.castShadow = true
        child.receiveShadow = true
      }
    })

    return c
  }, [trayModel, trayUrl, texture, swapUV, rotateQuarterTurns])

  // Calculate tray's bounding box after transformations
  const cloneBoxMinYRef = useRef<number | null>(null)
  if (clone && cloneBoxMinYRef.current === null) {
    const box = new THREE.Box3().setFromObject(clone)
    cloneBoxMinYRef.current = box.min.y
  }

  // Position tray based on topper **base** + dynamic visible height for non-adjustable beds.
  // The topper's bounding box sometimes includes hidden geometry, so we clamp the height to a reasonable range.
  // For adjustable beds, use bed top + hardcoded offset (to be tuned visually).
  const bedTopY = bedBoxMaxYRef.current ?? bedBox.max.y
  const ADJUSTABLE_BED_TRAY_OFFSET = -0.8 // Offset above bed top for adjustable beds (to be tuned)
  
  // Calculate visible topper height dynamically, clamping to reasonable range
  let topperHeight = 0.45 // Default fallback
  if (!isAdjustable && topperBaseY !== undefined && topperBaseY !== null && topperTopY !== undefined && topperTopY !== null) {
    const rawHeight = topperTopY - topperBaseY
    // Clamp to reasonable range: toppers are typically 0.3-1.0 units tall visually
    // If the bounding box includes hidden geometry (>1.0), use a sensible default
    topperHeight = rawHeight > 1.0 ? 0.45 : Math.max(0.3, Math.min(1.0, rawHeight))
  }
  
  const baseY = !isAdjustable && topperBaseY !== undefined && topperBaseY !== null && Number.isFinite(topperBaseY)
    ? topperBaseY + topperHeight
    : bedTopY + (isAdjustable ? ADJUSTABLE_BED_TRAY_OFFSET : 0)
  // We want the lowest point of the tray (cloneBoxMinY) to sit exactly on baseY.
  const trayY = baseY - (cloneBoxMinYRef.current ?? 0)

  return (
    <group position={[bedCenterX, trayY, bedCenterZ +2]}>
      <primitive object={clone} />
    </group>
  )
}

export default TrayCmp

